// Servicio Ficha Cliente: vista unificada y conversión a cliente
// GET /api/crm/contacts/:id/ficha (o adapter compuesto) + POST convert-to-client

import { api } from './api';
import { handleApiError } from '@/utils/errorHandler';
import type {
  ClienteFichaResponse,
  ConvertToClientRequest,
  ConvertToClientResponse,
  BillingPaymentItem,
  ContractMandateSummary,
  ContractMandateDetail,
  MandateItem,
  ExpedienteWorkspaceSummary,
  TimelineItem,
} from '@/types/clienteFicha';
import type { ContactExpedienteSummary } from '@/types/contactExpedienteWidget';
import { contactExpedienteWidgetService } from './contactExpedienteWidgetService';
import { crmService } from './crmService';
import { contractsService } from './contractsService';

const CRM_CONTACTS_BASE = '/crm/contacts';

/**
 * Obtiene la ficha cliente unificada para un contacto.
 * Intenta GET /api/crm/contacts/:contactId/ficha; si no existe (404), compone desde
 * contact + expediente-summary + contratos (adapter).
 */
export async function getClienteFicha(contactId: string): Promise<ClienteFichaResponse> {
  try {
    const { data } = await api.get<ClienteFichaResponse>(
      `${CRM_CONTACTS_BASE}/${contactId}/ficha`
    );
    return normalizeFichaResponse(data, contactId);
  } catch (err: unknown) {
    const status =
      err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;
    if (status === 404 || status === 501) {
      return composeFichaFromExistingApis(contactId);
    }
    throw handleApiError(err);
  }
}

/**
 * Normaliza la respuesta cruda del backend a los tipos frontend.
 * Mapea campos del backend (amount, status, hiring_code, at, meta, title)
 * a campos frontend (amount_cents, contract_status, contract_code, timestamp, metadata, titulo).
 */
export function normalizeFichaResponse(
  raw: Record<string, unknown> | ClienteFichaResponse,
  _contactId: string
): ClienteFichaResponse {
  // billing_payments: backend envía ClienteContractResponse (amount float EUR),
  // frontend espera BillingPaymentItem (amount_cents int)
  const billing = raw.billing_payments;
  const payments: BillingPaymentItem[] = Array.isArray(billing)
    ? billing.map((p: Record<string, unknown>) => ({
        id: String(p.id ?? ''),
        // Backend envía `amount` (float EUR), frontend usa `amount_cents` (int)
        amount_cents: p.amount_cents != null
          ? Number(p.amount_cents)
          : p.amount != null
          ? Math.round(Number(p.amount) * 100)
          : 0,
        currency: String(p.currency ?? 'eur'),
        status: String(p.status ?? ''),
        created_at: String(p.created_at ?? ''),
        description: p.description != null ? String(p.description)
          : p.service_name != null ? String(p.service_name) : undefined,
        invoice_id: p.invoice_id != null ? String(p.invoice_id) : undefined,
        charge_id: p.charge_id != null ? String(p.charge_id) : undefined,
      }))
    : [];

  // contract_mandate: backend envía ClienteFichaContractMandate (status, hiring_code, mandates[]),
  // frontend espera ContractMandateSummary (contract_status, contract_code, mandate_status)
  let contractMandate: ContractMandateSummary | null = null;
  const cm = raw.contract_mandate;
  if (cm != null && typeof cm === 'object') {
    const c = cm as Record<string, unknown>;
    const mandates = Array.isArray(c.mandates) ? c.mandates as Record<string, unknown>[] : [];
    const firstMandate = mandates[0];
    contractMandate = {
      // Backend: `status`, frontend: `contract_status`
      contract_status: String(c.contract_status ?? c.status ?? ''),
      // Backend: `hiring_code`, frontend: `contract_code`
      contract_code: c.contract_code != null ? String(c.contract_code)
        : c.hiring_code != null ? String(c.hiring_code) : undefined,
      mandate_status: c.mandate_status != null ? String(c.mandate_status)
        : firstMandate?.mandate_status != null ? String(firstMandate.mandate_status) : undefined,
      mandate_signed_at: c.mandate_signed_at != null ? String(c.mandate_signed_at)
        : firstMandate?.mandate_signed_at != null ? String(firstMandate.mandate_signed_at) : null,
      mandate_pdf_url: c.mandate_pdf_url != null ? String(c.mandate_pdf_url)
        : firstMandate?.mandate_pdf_url != null ? String(firstMandate.mandate_pdf_url) : null,
      final_contract_url: c.final_contract_url != null ? String(c.final_contract_url) : null,
    };
  }

  // contract_mandates: lista completa de contratos+mandatos (nuevo campo)
  const cms = raw.contract_mandates;
  const contractMandates: ContractMandateDetail[] = Array.isArray(cms)
    ? cms.map((item: Record<string, unknown>) => ({
        hiring_code: String(item.hiring_code ?? ''),
        service_name: String(item.service_name ?? ''),
        status: String(item.status ?? ''),
        contract_pdf_url: item.contract_pdf_url != null ? String(item.contract_pdf_url) : null,
        final_contract_url: item.final_contract_url != null ? String(item.final_contract_url) : null,
        mandates: Array.isArray(item.mandates)
          ? (item.mandates as Record<string, unknown>[]).map((m): MandateItem => ({
              mandate_code: String(m.mandate_code ?? ''),
              holder_index: Number(m.holder_index ?? 0),
              mandate_pdf_url: m.mandate_pdf_url != null ? String(m.mandate_pdf_url) : null,
              mandate_status: m.mandate_status != null ? String(m.mandate_status) : null,
              mandate_signed_at: m.mandate_signed_at != null ? String(m.mandate_signed_at) : null,
            }))
          : [],
      }))
    : [];

  // expediente_workspace: backend envía `title`, frontend usa `titulo`
  let expedienteWorkspace: ExpedienteWorkspaceSummary | null = null;
  const ew = raw.expediente_workspace;
  if (ew != null && typeof ew === 'object') {
    const e = ew as Record<string, unknown>;
    expedienteWorkspace = {
      expediente_id: String(e.expediente_id ?? ''),
      status: String(e.status ?? ''),
      titulo: e.titulo != null ? String(e.titulo) : e.title != null ? String(e.title) : undefined,
      formulario_codigo: e.formulario_codigo != null ? String(e.formulario_codigo) : null,
      formulario_nombre: e.formulario_nombre != null ? String(e.formulario_nombre) : null,
      completitud_pct: e.completitud_pct != null ? Number(e.completitud_pct) : undefined,
      next_action: e.next_action != null ? String(e.next_action) : null,
      next_steps: e.next_steps != null && typeof e.next_steps === 'object' ? (e.next_steps as Record<string, unknown>) : null,
      ai_status: e.ai_status != null ? String(e.ai_status) : null,
      riesgo_global: e.riesgo_global != null ? String(e.riesgo_global) : null,
      numero_expediente_oficial: e.numero_expediente_oficial != null ? String(e.numero_expediente_oficial) : null,
      faltantes_priorizados: Array.isArray(e.faltantes_priorizados) ? e.faltantes_priorizados : null,
      contact_id: e.contact_id != null ? String(e.contact_id) : undefined,
    };
  }

  // timeline: backend envía `at` y `meta`, frontend usa `timestamp` y `metadata`
  // Backend emite tipos extendidos (payment_completed, contract_signed) → mapeamos a canónicos
  const BACKEND_TYPE_MAP: Record<string, TimelineItem['type']> = {
    payment_completed: 'payment',
    contract_signed: 'signature',
    mandate_signed: 'signature',
  };
  const VALID_TYPES = new Set(['note', 'call', 'payment', 'signature', 'transcription', 'appointment']);
  const tl = raw.timeline;
  const timeline: TimelineItem[] = Array.isArray(tl)
    ? tl.map((t: Record<string, unknown>, idx: number) => {
        const rawType = String(t.type ?? 'note');
        const resolvedType = BACKEND_TYPE_MAP[rawType] ?? (VALID_TYPES.has(rawType) ? rawType : 'note');
        return {
          id: String(t.id ?? `tl-${idx}`),
          type: resolvedType as TimelineItem['type'],
          // Backend: `at`, frontend: `timestamp`
          timestamp: String(t.timestamp ?? t.at ?? ''),
          title: t.title != null ? String(t.title) : undefined,
          description: t.description != null ? String(t.description) : undefined,
          // Backend: `meta`, frontend: `metadata`
          metadata: (t.metadata ?? t.meta) != null && typeof (t.metadata ?? t.meta) === 'object'
            ? ((t.metadata ?? t.meta) as Record<string, unknown>)
            : undefined,
        };
      })
    : [];

  return {
    billing_payments: payments,
    contract_mandate: contractMandate,
    contract_mandates: contractMandates,
    expediente_workspace: expedienteWorkspace,
    timeline,
    solicitudes_pendientes_count: Number(raw.solicitudes_pendientes_count ?? 0),
  };
}

/**
 * Compone ClienteFichaResponse desde endpoints existentes cuando el backend
 * no expone aún GET /api/crm/contacts/:id/ficha.
 */
/** Resumen vacío cuando expediente-summary falla (404, red, etc.) para no romper la ficha compuesta. */
function emptyExpedienteSummary(contactId: string): ContactExpedienteSummary {
  return {
    contact_id: contactId,
    expediente_id: null,
    expediente_number: 'Sin numero de expediente',
    tramite_status: 'unknown',
    next_action_suggested: '',
    contrato_vigente: null,
    is_partial: false,
  };
}

async function composeFichaFromExistingApis(contactId: string): Promise<ClienteFichaResponse> {
  const [contact, summary, contractsResponse] = await Promise.all([
    crmService.getContact(contactId, false).catch(() => null),
    contactExpedienteWidgetService
      .getContactExpedienteSummary(contactId)
      .catch(() => emptyExpedienteSummary(contactId)),
    contractsService.getContracts({ limit: 50 }).catch(() => ({ items: [], total: 0, skip: 0, limit: 50 })),
  ]);

  const contracts = contractsResponse.items.filter((c) => c.contact_id === contactId);
  const primary = contracts.find((c) => c.status === 'paid' || c.status === 'completed') ?? contracts[0];

  const billing_payments: BillingPaymentItem[] = [];
  if (primary?.first_payment_amount != null) {
    billing_payments.push({
      id: primary.id,
      amount_cents: primary.first_payment_amount * 100,
      currency: primary.currency ?? 'eur',
      status: primary.status ?? 'pending',
      created_at: primary.created_at ?? '',
      description: primary.service_name,
    });
  }

  let contract_mandate: ContractMandateSummary | null = null;
  if (primary) {
    contract_mandate = {
      contract_status: primary.status ?? 'pending',
      contract_code: primary.hiring_code,
      mandate_status: primary.mandate_status ?? undefined,
      mandate_signed_at: primary.mandate_signed_at ?? null,
      mandate_pdf_url: primary.mandate_pdf_url ?? null,
      final_contract_url: primary.final_contract_url ?? null,
    };
  }

  let expediente_workspace: ExpedienteWorkspaceSummary | null = null;
  if (summary.expediente_id) {
    expediente_workspace = {
      expediente_id: summary.expediente_id,
      status: summary.tramite_status,
      next_action: summary.next_action_suggested,
      contact_id: contactId,
    };
    if (summary.contrato_vigente) {
      expediente_workspace.completitud_pct = summary.contrato_vigente.status === 'paid' ? 100 : undefined;
    }
  }

  const timeline: TimelineItem[] = [];
  if (contact?.updated_at) {
    timeline.push({
      id: `contact-${contactId}`,
      type: 'note',
      timestamp: contact.updated_at,
      title: 'Contacto actualizado',
      description: contact.name,
    });
  }

  return {
    billing_payments,
    contract_mandate,
    contract_mandates: [],
    expediente_workspace,
    timeline: timeline.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    solicitudes_pendientes_count: 0,
  };
}

/**
 * Convierte un contacto a cliente (solo admin).
 * POST /api/crm/contacts/:contact_id/convert-to-client
 */
export async function convertContactToClient(
  contactId: string,
  body?: ConvertToClientRequest
): Promise<ConvertToClientResponse> {
  const { data } = await api.post<ConvertToClientResponse>(
    `${CRM_CONTACTS_BASE}/${contactId}/convert-to-client`,
    body ?? {}
  );
  return {
    contact_id: data.contact_id,
    user_id: data.user_id,
    converted: data.converted,
  };
}
